import type { ReactNode } from 'react';
import { alpha, styled } from '../styles';

import Box from '../Box';

export type VARIANT =
  | 'default'
  | 'alternative'
  | 'brandedLight'
  | 'alternativeLight'
  | 'secondary'
  | 'marketing';
export type PADDING = 'default' | 'defaultBottom' | 'defaultTop' | 'none' | 'reduced';

export interface Props {
  variant: VARIANT;
  paddingVariant: PADDING;
  customColor?: string;
  children: ReactNode;
  id?: string;
  className?: string;
  dataTestId?: string;
  hasStyledBorders?: boolean;
}

const StyledSection = styled(Box, {
  shouldForwardProp: (prop) =>
    !(['variant', 'paddingVariant', 'customColor', 'hasStyledBorders'] as unknown[]).includes(prop),
})<Props>(
  ({ theme: { spacing, palette }, variant, customColor, paddingVariant, hasStyledBorders }) => {
    const getBackgroundColor = (bgVariant: VARIANT) => {
      if (customColor && variant === 'marketing') {
        return customColor;
      }
      const variantToColor = {
        alternative: palette.layout.background.alternative,
        default: 'transparent',
        brandedLight: alpha(palette.primary.main, 0.04),
        alternativeLight: '#FAFAFA',
        secondary: palette.text.onMainSecondary,
      } as Record<VARIANT, string>;
      return variantToColor[bgVariant];
    };

    const getPadding = (padding: PADDING) => {
      const paddingToValue = {
        none: 0,
        reduced: spacing(4, 0),
        default: spacing(9, 0),
        defaultBottom: spacing(0, 0, 9, 0),
        defaultTop: spacing(9, 0, 0, 0),
      } as Record<PADDING, number | string>;
      return paddingToValue[padding];
    };
    const borderColor = customColor ?? palette.primary.main;
    return {
      backgroundColor: getBackgroundColor(variant),
      padding: getPadding(paddingVariant),
      ...{
        ...(hasStyledBorders
          ? {
              position: 'relative',
              '&:before': {
                background: `linear-gradient(90deg, ${alpha(
                  borderColor,
                  0.24
                )} 0%, ${borderColor} 100%)`,
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                height: 1,
                width: '50%',
              },
              '&:after': {
                background: `linear-gradient(90deg, ${borderColor} 0%, ${alpha(
                  borderColor,
                  0.24
                )} 100%)`,
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                height: 1,
                width: '50%',
              },
            }
          : {}),
      },
    };
  }
);

const Section = ({
  dataTestId,
  children,
  variant = 'default',
  paddingVariant = 'default',
  ...props
}: Props) => (
  <StyledSection
    component="section"
    data-testid={dataTestId}
    variant={variant}
    paddingVariant={paddingVariant}
    {...props}
  >
    {children}
  </StyledSection>
);

export default Section;
